import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  TextField,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  Tooltip,
  Typography,
  DialogActions,
} from '@mui/material';
import {
  StyledTableHeaderCell
} from "../TableStyles";
import {
  Edit,
  Delete,
  Group,
  Close,
  Save,
  Cancel,
  Warning,
  PersonAdd,
  Shield,
} from '@mui/icons-material';
import apiClient from '../../../utils/axios';

const ManageUsersDialog = ({ open, onClose }) => {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    name: '',
    lastname: '',
    role: 'operator',
    is_active: true,
  });

  const protectedUsers = ['admin', 'guardia1', 'operador', 'supervisor'];
  const roles = [
    { value: 'admin', label: 'Administrador' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'operator', label: 'Operador' },
    { value: 'guard', label: 'Guardia' },
  ];

  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      setError('');
      const response = await apiClient.get('/api/auth/users?include_inactive=true');
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      setError('Error al cargar usuarios');
    }
  };

  const handleCreateUser = async () => {
    try {
      setError('');
      setSuccess('');

      if (!formData.username || !formData.email || !formData.password || 
          !formData.name || !formData.lastname) {
        setError('Todos los campos son requeridos');
        return;
      }

      if (formData.password.length < 6) {
        setError('La contraseña debe tener al menos 6 caracteres');
        return;
      }

      const response = await apiClient.post('/api/auth/users', formData);

      if (response.data.success) {
        setSuccess('Usuario creado exitosamente');
        setShowCreateForm(false);
        setFormData({
          username: '',
          email: '',
          password: '',
          name: '',
          lastname: '',
          role: 'operator',
          is_active: true,
        });
        loadUsers();
      }
    } catch (error) {
      console.error('Error creando usuario:', error);
      setError(error.response?.data?.error || 'Error al crear usuario');
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      setError('');
      setSuccess('');

      const response = await apiClient.put(`/api/auth/users/${userId}`, updates);

      if (response.data.success) {
        setSuccess('Usuario actualizado exitosamente');
        setEditingUser(null);
        loadUsers();
      }
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      setError(error.response?.data?.error || 'Error al actualizar usuario');
    }
  };

  const handleToggleActive = async (user) => {
    await handleUpdateUser(user.id, { is_active: !user.is_active });
  };

  const handleDeleteUser = async (userId, username) => {
    if (protectedUsers.includes(username)) {
      setError(`El usuario "${username}" no puede eliminarse (usuario protegido)`);
      return;
    }

    try {
      setError('');
      setSuccess('');

      const response = await apiClient.delete(`/api/auth/users/${userId}`);

      if (response.data.success) {
        setSuccess('Usuario eliminado exitosamente');
        loadUsers();
      }
      setConfirmDelete(false);
    } catch (error) {
      setConfirmDelete(false);
      console.error('Error eliminando usuario:', error);
      setError(error.response?.data?.error || 'Error al eliminar usuario');
    }
  };

  const isProtectedUser = (username) => {
    return protectedUsers.includes(username);
  };

  const getRoleLabel = (role) => {
    const roleObj = roles.find((r) => r.value === role);
    return roleObj ? roleObj.label : role;
  };

  const handleClose = () => {
    setShowCreateForm(false);
    setEditingUser(null);
    setError('');
    setSuccess('');
    onClose();
  };

  return (
  <>
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #393939 0%, #4a4a4a 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Group />
          Administración de Usuarios
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {!showCreateForm ? (
          <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={() => setShowCreateForm(true)}
                sx={{
                  background: '#f3a92a',
                  '&:hover': { background: '#e4ae52' },
                }}
              >
                Crear Nuevo Usuario
              </Button>
            </Box>

            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledTableHeaderCell sx={{ fontWeight: 'bold'}}>
                      Usuario
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell sx={{ fontWeight: 'bold'}}>
                      Nombre Completo
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell sx={{ fontWeight: 'bold'}}>
                      Email
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell sx={{ fontWeight: 'bold'}}>
                      Rol
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell sx={{ fontWeight: 'bold'}}>
                      Estado
                    </StyledTableHeaderCell>
                    <StyledTableHeaderCell sx={{ fontWeight: 'bold'}}>
                      Acciones
                    </StyledTableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {user.username}
                          {isProtectedUser(user.username) && (
                            <Tooltip title="Usuario protegido">
                              <Shield fontSize="small" color="warning" />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {editingUser?.id === user.id ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField
                              size="small"
                              value={editingUser.name}
                              onChange={(e) =>
                                setEditingUser({ ...editingUser, name: e.target.value })
                              }
                              sx={{ width: 100 }}
                            />
                            <TextField
                              size="small"
                              value={editingUser.lastname}
                              onChange={(e) =>
                                setEditingUser({ ...editingUser, lastname: e.target.value })
                              }
                              sx={{ width: 100 }}
                            />
                          </Box>
                        ) : (
                          `${user.name} ${user.lastname}`
                        )}
                      </TableCell>
                      <TableCell>
                        {editingUser?.id === user.id ? (
                          <TextField
                            size="small"
                            value={editingUser.email}
                            onChange={(e) =>
                              setEditingUser({ ...editingUser, email: e.target.value })
                            }
                            fullWidth
                          />
                        ) : (
                          user.email
                        )}
                      </TableCell>
                      <TableCell>
                        {editingUser?.id === user.id ? (
                          <FormControl size="small" fullWidth>
                            <Select
                              value={editingUser.role}
                              onChange={(e) =>
                                setEditingUser({ ...editingUser, role: e.target.value })
                              }
                            >
                              {roles.map((role) => (
                                <MenuItem key={role.value} value={role.value}>
                                  {role.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            label={getRoleLabel(user.role)}
                            size="small"
                            color={
                              user.role === 'admin'
                                ? 'error'
                                : user.role === 'supervisor'
                                ? 'warning'
                                : 'default'
                            }
                          />
                        )}
                      </TableCell>
                        <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={editingUser?.id === user.id ? editingUser.is_active : user.is_active}
                              onChange={() => {
                                if (editingUser?.id === user.id) {
                                  setEditingUser({ ...editingUser, is_active: !editingUser.is_active });
                                } else {
                                  handleToggleActive(user);
                                }
                              }}
                              color="success"
                            />
                          }
                          label={
                            (editingUser?.id === user.id ? editingUser.is_active : user.is_active) ? 'Activo' : 'Inactivo'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {editingUser?.id === user.id ? (
                            <>
                              <Tooltip title="Guardar">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() =>
                                    handleUpdateUser(user.id, {
                                      name: editingUser.name,
                                      lastname: editingUser.lastname,
                                      email: editingUser.email,
                                      role: editingUser.role,
                                      is_active: editingUser.is_active,
                                    })
                                  }
                                >
                                  <Save fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancelar">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setEditingUser(null)}
                                >
                                  <Cancel fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => setEditingUser({ ...user })}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {!isProtectedUser(user.username) && (
                                <Tooltip title="Eliminar">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                        setConfirmDelete(true);
                                        setSelectedUser(user);
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, background: "#f3a92a", padding: 0.5, borderRadius: 0.9 }}>
              <h3 style={{ color: "white" }}>Crear Nuevo Usuario</h3>
            </Box>

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
              <TextField
                label="Usuario"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Nombre"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Apellido"
                value={formData.lastname}
                onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                required
                fullWidth
              />
              <TextField
                label="Contraseña"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                fullWidth
                helperText="Mínimo 6 caracteres"
              />
              <FormControl fullWidth required>
                <InputLabel>Rol</InputLabel>
                <Select
                  value={formData.role}
                  label="Rol"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setShowCreateForm(false)}
                startIcon={<Cancel />}
                sx={{
                  borderColor: '#393939ff',
                  color: '#393939ff',
                  '&:hover': { 
                    borderColor: '#2a2a2a',
                    backgroundColor: 'rgba(57, 57, 57, 0.04)'
                  },
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateUser}
                startIcon={<Save />}
                sx={{
                  background: '#393939ff',
                  '&:hover': { background: '#2a2a2a' },
                }}
              >
                Crear Usuario
              </Button>
            </Box>
          </Paper>
        )}
      </DialogContent>
    </Dialog>
    <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Warning color="warning" />
            <Typography variant="h6">Confirmar</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>¿Está seguro de eliminar el usuario? Esta acción no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} color="inherit">
            Cancelar
          </Button>
          <Button onClick={()=> handleDeleteUser(selectedUser?.id, selectedUser?.username)} variant="contained" color="warning">
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      </>
  );
};

export default ManageUsersDialog;